// Base libs
import { useContext, useState } from "react";
import { shallowEqual } from "react-redux";
import {
    GuxButton,
    GuxButtonMulti,
    GuxIcon,
    GuxListItem,
    GuxTooltip,
} from "genesys-spark-components-react";

// Styles
import "./QuestionGroupList.less";

// General common
import { useAppDispatch, useAppSelector } from "app/hooks";
import FormType from "enums/FormType";
import { DefaultLimits } from "constants/DefaultLimits";

// Features common
import { Card } from "features/common/Card";
import { TextLine } from "features/common/TextLine";
import { LevelContext } from "features/qualityForm/common/Contexts";

// Feature toggles
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";

// Feature slices
import {
    addQuestionFromSavedTemplate,
    qualityFormActions,
    selectDoesNpsQuestionAlreadyExist,
    selectQuestionGroup,
    selectCanEditForm,
    selectFormLanguage,
} from "features/qualityForm/qualityFormSlice";
import {
    getQuestionTemplate,
    selectForm,
    selectFormType,
    selectIsAutomatedEvaluationForm,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import {
    selectFavouriteTemplateIds,
    selectUnFavouredTemplateIds,
} from "features/templates/templateSlice";

// Locales
import { useStrings } from "features/i18n/useStrings";
import { useLocale } from "features/i18n/useLocale";
import strings from "./QuestionGroupList.strings";

import {
    selectIsQuestionGroupQuestionCountValid,
    selectIsQuestionGroupValid,
    selectIsQuestionValid,
    selectIsQuestionCountAtOrAboveLimit,
} from "features/qualityForm/qualityFormValidations";

// Form Editor
import { SubQuestionType, getQuestionTypesMap } from "../common/QuestionType";
import { TemplateController, TemplateOperation } from "../ManageTemplate";
import { ErrorStatement } from "../common/ErrorStatement/ErrorStatement";
import { Question } from "../question/Question";
import { QuestionGroup } from "./QuestionGroup";

export function QuestionList({ questionGroupId }: { questionGroupId: string }) {
    const questionIds =
        useAppSelector(
            (state) => selectQuestionGroup(state, questionGroupId)?.questions
        ) ?? [];
    const { level } = useContext(LevelContext);

    const questions = questionIds.map((questionId, index) => {
        return (
            <LevelContext.Provider
                value={{
                    level: level,
                    subLevel: index + 1,
                }}
                key={questionId}
            >
                <QuestionListItem questionId={questionId} key={questionId} />
            </LevelContext.Provider>
        );
    });

    return (
        <div className="QuestionList" data-testid="questionList">
            {questions}
        </div>
    );
}

function QuestionListItem({ questionId }: { questionId: string }) {
    const isQuestionValid = useAppSelector((state) =>
        selectIsQuestionValid(state, questionId)
    );
    return (
        <div className="QuestionListEntry" data-testid="questionListEntry">
            <Card className={`${isQuestionValid ? "" : "error"}`}>
                <Question questionId={questionId} />
            </Card>
        </div>
    );
}

function AddQuestion({ questionGroupId }: { questionGroupId: string }) {
    const t = useStrings(strings);

    const formType = useAppSelector(selectFormType);
    const formLanguage = useAppSelector(selectFormLanguage);
    const locale = useLocale();
    const dispatch = useAppDispatch();
    const npsAlreadyExists = useAppSelector((state) => {
        if (formType === FormType.SURVEY) {
            return selectDoesNpsQuestionAlreadyExist(state);
        } else return false;
    });
    const isQuestionCountAtOrAboveLimit = useAppSelector(
        selectIsQuestionCountAtOrAboveLimit
    );
    const isMultipleSelectFTEnabled = useAppSelector((state) => {
        return selectFeatureToggle(state, FeatureToggle.MULTIPLE_SELECT);
    });

    const isAutomatedEvaluationForm = useAppSelector((state) => {
        return selectIsAutomatedEvaluationForm(state);
    });

    const includeMultipleSelect =
        isMultipleSelectFTEnabled && !isAutomatedEvaluationForm;

    const questionTypesMap = getQuestionTypesMap(includeMultipleSelect);

    return formType ? (
        <div className="AddQuestion">
            <GuxButtonMulti
                accent="secondary"
                disabled={isQuestionCountAtOrAboveLimit || undefined}
                aria-disabled={isQuestionCountAtOrAboveLimit || undefined}
                data-testid="add-question-button"
            >
                <span slot="title">{t.addQuestion.label}</span>
                {questionTypesMap[formType].map((questionInfo) => {
                    const isNpsType =
                        questionInfo.subQuestionType === SubQuestionType.NPS;
                    return (
                        <GuxListItem
                            key={questionInfo.subQuestionType}
                            onClick={() => {
                                dispatch(
                                    qualityFormActions.addQuestionToQuestionGroup(
                                        {
                                            questionGroupId,
                                            template: getQuestionTemplate(
                                                questionInfo,
                                                formLanguage ?? locale,
                                                isMultipleSelectFTEnabled
                                            ),
                                            showEditMode: true,
                                        }
                                    )
                                );
                            }}
                            disabled={
                                isNpsType && npsAlreadyExists ? true : undefined
                            }
                            aria-disabled={
                                isNpsType && npsAlreadyExists ? true : undefined
                            }
                        >
                            {t.addQuestion.type[questionInfo.subQuestionType]}
                        </GuxListItem>
                    );
                })}
            </GuxButtonMulti>
            {isQuestionCountAtOrAboveLimit && (
                <GuxTooltip>
                    {t.labels.questionLimitReachedTooltip({
                        maxQuestionCount: DefaultLimits.maxQuestionCount,
                    })}
                </GuxTooltip>
            )}
        </div>
    ) : (
        <></>
    );
}

function AddTemplateButton({ questionGroupId }: { questionGroupId: string }) {
    const dispatch = useAppDispatch();

    const regularTemplateIds = useAppSelector(
        selectUnFavouredTemplateIds,
        shallowEqual
    );
    const favouriteTemplateIds = useAppSelector(
        selectFavouriteTemplateIds,
        shallowEqual
    );

    const [isTemplateAdderOpen, setIsTemplateAdderOpen] = useState(false);
    const onAddTemplate = (templateId: string) => {
        dispatch(addQuestionFromSavedTemplate(questionGroupId, templateId));
        setIsTemplateAdderOpen(false);
    };

    const isQuestionCountAtOrAboveLimit = useAppSelector(
        selectIsQuestionCountAtOrAboveLimit
    );

    const t = useStrings(strings);

    return regularTemplateIds.length || favouriteTemplateIds.length ? (
        <div className="AddTemplate">
            <GuxButton
                accent="secondary"
                onClick={() => {
                    setIsTemplateAdderOpen(true);
                }}
                disabled={isQuestionCountAtOrAboveLimit || undefined}
                aria-disabled={isQuestionCountAtOrAboveLimit || undefined}
            >
                <GuxIcon iconName="add" decorative={true}></GuxIcon>
                <span>{t.addQuestionTemplate.label}</span>
            </GuxButton>
            {isQuestionCountAtOrAboveLimit && (
                <GuxTooltip>
                    {t.labels.questionLimitReachedTooltip({
                        maxQuestionCount: DefaultLimits.maxQuestionCount,
                    })}
                </GuxTooltip>
            )}
            {isTemplateAdderOpen && (
                <TemplateController
                    isTemplateManagerOpen={isTemplateAdderOpen}
                    close={() => setIsTemplateAdderOpen(false)}
                    templateOperation={TemplateOperation.add}
                    addTemplate={onAddTemplate}
                />
            )}
        </div>
    ) : (
        <></>
    );
}

function QuestionGroupListCounter() {
    const t = useStrings(strings);

    const totalGroupsCount = useAppSelector(
        (state) => selectForm(state)?.questionGroups?.length ?? 0
    );
    const { level } = useContext(LevelContext);
    return (
        <TextLine>
            {t.labels.questionGroupNumber({
                groupCount: level,
                totalGroupsCount,
            })}
        </TextLine>
    );
}

function QuestionGroupListEntry({ id }: { id: string }) {
    const t = useStrings(strings);

    const canEditForm = useAppSelector((state) => {
        return selectCanEditForm(state);
    });
    const hasValidQuestionCount = useAppSelector((state) =>
        selectIsQuestionGroupQuestionCountValid(state, id)
    );

    const hasTemplateFunctionality = useAppSelector(
        (state) => selectFormType(state) === FormType.EVALUATION
    );
    return (
        <div
            className="QuestionGroupListEntry"
            data-testid="questionGroupListEntry"
        >
            <QuestionGroupListCounter />
            <Card className="filled">
                <div
                    className="QuestionGroupListEntryContents"
                    data-testid="questionGroupListEntryContents"
                >
                    <QuestionGroupCard id={id} />
                    {!hasValidQuestionCount ? (
                        <div className="QuestionGroupListEntryContentsError">
                            <ErrorStatement
                                errorMessage={t.error.questionCount}
                            />
                        </div>
                    ) : (
                        <QuestionList questionGroupId={id} />
                    )}
                    <div className="footer">
                        {canEditForm && (
                            <>
                                <AddQuestion questionGroupId={id} />
                                {hasTemplateFunctionality && (
                                    <AddTemplateButton questionGroupId={id} />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </Card>
        </div>
    );
}

function QuestionGroupCard({ id }: { id: string }) {
    const isValid = useAppSelector((state) =>
        selectIsQuestionGroupValid(state, id)
    );
    return (
        <Card className={`${isValid ? "" : "error"}`}>
            <QuestionGroup questionGroupId={id} />
        </Card>
    );
}

export function QuestionGroupList() {
    const questionGroupIds =
        useAppSelector((state) => selectForm(state)?.questionGroups) ?? [];

    const questionGroups = questionGroupIds.map((questionGroupId, index) => {
        return (
            <LevelContext.Provider
                value={{
                    level: index + 1,
                    subLevel: 0,
                }}
                key={questionGroupId}
            >
                <QuestionGroupListEntry id={questionGroupId} />
            </LevelContext.Provider>
        );
    });
    return <div className="QuestionGroupList">{questionGroups}</div>;
}
